/**
 * Public User Permission Types
 * Only exposes minimal necessary information for client-side access control
 */

export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager', 
  TRADER = 'trader',
  CLIENT = 'client',
  VIEWER = 'viewer',
  AUDITOR = 'auditor'
}

export enum PermissionLevel {
  FULL = 'full',
  LIMITED = 'limited',
  READ_ONLY = 'read_only',
  RESTRICTED = 'restricted'
}

/**
 * Client-side user context (minimal exposure)
 */
export interface UserPermissions {
  role: UserRole;
  level: PermissionLevel;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  restrictedToOwnData: boolean;
}

/**
 * Get default permissions for role (fallback only - server determines actual permissions)
 * WARNING: These are fallback permissions only. Real permissions come from the server.
 * Do not rely on these for security decisions.
 */
export function getUserPermissions(role: UserRole): UserPermissions {
  // NOTE: This function provides fallback/default permissions for UI rendering only
  // The actual permissions are determined server-side and should always be fetched
  // from the security validation API endpoints
  
  switch (role) {
    case UserRole.ADMIN:
      return {
        role,
        level: PermissionLevel.FULL,
        canRead: true,
        canWrite: true,
        canDelete: true,
        restrictedToOwnData: false
      };
      
    case UserRole.MANAGER:
      return {
        role,
        level: PermissionLevel.LIMITED,
        canRead: true,
        canWrite: true,
        canDelete: false,
        restrictedToOwnData: false
      };
      
    case UserRole.TRADER:
      return {
        role,
        level: PermissionLevel.LIMITED,
        canRead: true,
        canWrite: true,
        canDelete: false,
        restrictedToOwnData: true
      };
      
    case UserRole.CLIENT:
      return {
        role,
        level: PermissionLevel.LIMITED,
        canRead: true,
        canWrite: true,
        canDelete: false,
        restrictedToOwnData: true
      };
      
    case UserRole.AUDITOR:
      return {
        role,
        level: PermissionLevel.READ_ONLY,
        canRead: true,
        canWrite: false,
        canDelete: false,
        restrictedToOwnData: false
      };
      
    case UserRole.VIEWER:
    default:
      return {
        role,
        level: PermissionLevel.RESTRICTED,
        canRead: true,
        canWrite: false,
        canDelete: false,
        restrictedToOwnData: true
      };
  }
}

/**
 * Check if user can access specific features (UI hint only)
 * WARNING: This is for UI rendering hints only. Always validate access server-side.
 * Real feature access must be validated through security validation API endpoints.
 */
export function canAccessFeature(role: UserRole, feature: string): boolean {
  // NOTE: This function provides UI rendering hints only
  // Real feature access control happens server-side with proper security validation
  // Always call validateUserAccess() or authorizeOperation() for actual access control
  
  const permissions = getUserPermissions(role);
  
  // Basic feature access hints based on role (not authoritative)
  switch (feature) {
    case 'dashboard':
      return permissions.canRead;
    case 'trading':
      return [UserRole.ADMIN, UserRole.MANAGER, UserRole.TRADER, UserRole.CLIENT].includes(role);
    case 'reports':
      return permissions.canRead;
    case 'settings':
      return permissions.canWrite;
    case 'admin':
      return role === UserRole.ADMIN;
    case 'audit':
      return [UserRole.ADMIN, UserRole.AUDITOR].includes(role);
    default:
      return false;
  }
}